import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import metadataJson from '@/data/metatags.json';

interface MetadataItem {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  ogType: string;
  twitterCard: string;
  canonical: string;
  keywords: string;
}

interface MetadataCollection {
  [path: string]: MetadataItem;
}

// Type assertion for the imported JSON
const metadata = metadataJson as MetadataCollection;

interface SEOHelmetProps {
  /**
   * Optional path override. If not provided, will use current URL path
   */
  path?: string;
}

export const SEOHelmet: React.FC<SEOHelmetProps> = ({ path }) => {
  const location = useLocation();
  const { t } = useTranslation('common');
  
  // Use provided path or current location path
  const currentPath = path || location.pathname;
  
  // Use root path as fallback if no matching path is found
  const metadataPath = metadata[currentPath] ? currentPath : '/';
  const meta = metadata[metadataPath];

  // Translation keys based on path
  const translationNamespace = metadataPath === '/' 
    ? 'homepage' 
    : metadataPath.replace('/', ''); // Remove leading slash

  return (
    <Helmet>
      {/* Title with translation fallback */}
      <title>{t(`${translationNamespace}.helmet.title`, { defaultValue: meta.title })}</title>
      
      {/* Basic meta tags with translation fallbacks */}
      <meta 
        name="description" 
        content={t(`${translationNamespace}.helmet.description`, { defaultValue: meta.description })} 
      />
      <meta 
        name="keywords" 
        content={t(`${translationNamespace}.helmet.keywords`, { defaultValue: meta.keywords })} 
      />
      
      {/* Open Graph tags */}
      <meta 
        property="og:title" 
        content={t(`${translationNamespace}.helmet.ogTitle`, { defaultValue: meta.ogTitle })} 
      />
      <meta 
        property="og:description" 
        content={t(`${translationNamespace}.helmet.ogDescription`, { defaultValue: meta.ogDescription })} 
      />
      <meta property="og:image" content={meta.ogImage} />
      <meta property="og:url" content={meta.ogUrl} />
      <meta property="og:type" content={meta.ogType} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={meta.twitterCard} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={meta.canonical} />
    </Helmet>
  );
};

export default SEOHelmet;